import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProjectById } from '@/mocks/projectGalleryData';
import type { ProjectGallery } from '@/mocks/projectGalleryData';
import Footer from '@/pages/home/components/Footer';

function ProjectHeader() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-editorial ${
        scrolled
          ? 'bg-white/95 backdrop-blur-[12px] shadow-[0_1px_0_0_rgba(0,0,0,0.05)]'
          : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between h-16 md:h-20 px-6 md:px-12 lg:px-20">
        <Link to="/" className="flex items-center">
          <img
            src="https://storage.readdy-site.link/project_files/984fb076-7af4-4ebe-bc63-07e20452888b/0a9ff90d-afe9-4bcc-baa6-21d12203b4d5_LOGO-MARCA-D_GUA.png?v=5a326700360f55983d50a6f422058b16"
            alt="Stephanie Zarowny"
            className="h-8 md:h-9 w-auto object-contain"
          />
        </Link>
        <Link
          to="/"
          className="font-body text-sm font-light text-black hover:text-marrom transition-colors duration-300 whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
          Voltar
        </Link>
      </div>
    </header>
  );
}

function Lightbox({
  images,
  currentIndex,
  onClose,
  onNext,
  onPrev,
}: {
  images: { src: string; alt: string }[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onNext, onPrev]);

  const img = images[currentIndex];

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 md:top-8 md:right-8 text-white/70 hover:text-white transition-colors z-10"
        aria-label="Fechar"
      >
        <i className="ri-close-line w-10 h-10 flex items-center justify-center text-3xl" />
      </button>

      {/* Prev */}
      <button
        onClick={onPrev}
        className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors z-10"
        aria-label="Anterior"
      >
        <i className="ri-arrow-left-s-line w-10 h-10 flex items-center justify-center text-3xl md:text-4xl" />
      </button>

      {/* Image */}
      <div className="max-w-[90vw] max-h-[85vh] px-12 md:px-20">
        <img
          src={img.src}
          alt={img.alt}
          className="max-w-full max-h-[85vh] object-contain"
        />
      </div>

      {/* Next */}
      <button
        onClick={onNext}
        className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors z-10"
        aria-label="Próxima"
      >
        <i className="ri-arrow-right-s-line w-10 h-10 flex items-center justify-center text-3xl md:text-4xl" />
      </button>

      {/* Counter */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/50 font-body text-xs font-light">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
}

export default function ProjectPage() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<ProjectGallery | undefined>();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [loaded, setLoaded] = useState<Set<number>>(new Set());
  const infoRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) {
      const p = getProjectById(id);
      setProject(p);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [id]);

  useEffect(() => {
    if (!infoRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0', 'translate-y-6');
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = infoRef.current.querySelectorAll('[data-animate]');
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [project]);

  useEffect(() => {
    if (!galleryRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute('data-index'));
            if (!Number.isNaN(idx)) {
              setLoaded((prev) => new Set(prev).add(idx));
            }
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    const els = galleryRef.current.querySelectorAll('[data-index]');
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [project]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="font-display text-2xl font-light text-black mb-4">
            Projeto não encontrado
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-light text-white bg-marrom rounded-full hover:bg-cinza-marca transition-colors duration-300"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center mr-2" />
            Voltar ao início
          </Link>
        </div>
      </div>
    );
  }

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () =>
    setLightboxIndex((prev) => (prev + 1) % project.images.length);
  const prevImage = () =>
    setLightboxIndex(
      (prev) => (prev - 1 + project.images.length) % project.images.length
    );

  return (
    <div className="min-h-screen bg-white">
      <ProjectHeader />

      {/* Hero image */}
      <section className="pt-16 md:pt-20">
        <div className="w-full h-[45vh] md:h-[60vh] lg:h-[70vh] overflow-hidden relative">
          <img
            src={project.mainImage}
            alt={project.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          <div className="absolute bottom-6 md:bottom-10 left-6 md:left-12 lg:left-20">
            <span className="font-body text-white/70 text-xs font-light uppercase tracking-[0.12em] block mb-2">
              {project.category}
            </span>
            <h1 className="font-display text-white text-2xl md:text-4xl lg:text-5xl font-extralight tracking-tight">
              {project.name}
            </h1>
          </div>
        </div>
      </section>

      {/* Project info */}
      <section className="py-14 md:py-20 lg:py-24" ref={infoRef}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div
            data-animate
            className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 lg:gap-20 opacity-0 translate-y-6 transition-all duration-700 ease-editorial"
          >
            {/* Meta */}
            <div className="flex flex-col gap-6">
              <div>
                <span className="font-body text-xs font-light uppercase tracking-[0.1em] text-brand-gray-mid block mb-1">
                  Localização
                </span>
                <p className="font-body text-sm md:text-base font-light text-black">
                  {project.location}
                </p>
              </div>
              <div>
                <span className="font-body text-xs font-light uppercase tracking-[0.1em] text-brand-gray-mid block mb-1">
                  Ano
                </span>
                <p className="font-body text-sm md:text-base font-light text-black">
                  {project.year}
                </p>
              </div>
              <div>
                <span className="font-body text-xs font-light uppercase tracking-[0.1em] text-brand-gray-mid block mb-1">
                  Categoria
                </span>
                <p className="font-body text-sm md:text-base font-light text-black">
                  {project.category}
                </p>
              </div>
            </div>

            {/* Description */}
            <div>
              <h2 className="font-display text-xl md:text-2xl font-extralight text-black tracking-tight mb-5">
                Sobre o projeto
              </h2>
              <p className="font-body text-sm md:text-base font-light text-brand-gray-body leading-relaxed">
                {project.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="pb-20 md:pb-28 lg:pb-36" ref={galleryRef}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {project.images.map((img, idx) => {
              const isLoaded = loaded.has(idx);

              return (
                <button
                  key={idx}
                  data-index={idx}
                  onClick={() => openLightbox(idx)}
                  className={`group relative overflow-hidden cursor-pointer aspect-[3/4] ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-700 ease-editorial`}
                  style={{ transitionDelay: `${(idx % 3) * 100}ms` }}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover object-top transition-transform duration-[600ms] ease-in-out group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  {!(project.id === '4' && (idx === 4 || idx === 5)) && !(project.id === '5' && (idx === 4 || idx === 5)) && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <span className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100">
                        <i className="ri-zoom-in-line w-5 h-5 flex items-center justify-center text-lg" />
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Back CTA */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 text-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center px-8 py-3.5 text-sm font-light text-white bg-marrom rounded-full hover:bg-cinza-marca transition-colors duration-300 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center mr-2" />
            Ver todos os projetos
          </Link>
        </div>
      </section>

      <Footer />

      {lightboxOpen && (
        <Lightbox
          images={project.images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNext={nextImage}
          onPrev={prevImage}
        />
      )}
    </div>
  );
}